import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable } from 'rxjs';

/**
 * Service for sharing data between components with localStorage persistence for critical data
 * This provides both reactive data sharing and persistence across page refreshes
 */
@Injectable({
  providedIn: 'root'
})
export class DataSharingService {
  // Storage keys
  private readonly OPERATION_DATA_KEY = 'tlr_operation_data';
  private readonly TAG_NUMBER_KEY = 'tlr_tag_number';
  
  // Private BehaviorSubjects for different data types
  private operationDataSubject = new BehaviorSubject<any>(null);
  
  // Other data subjects can be added here as needed
  // private engineerDataSubject = new BehaviorSubject<any>(null);
  // private airDataSubject = new BehaviorSubject<any>(null);
  // private inspectionDataSubject = new BehaviorSubject<any>(null);
  
  constructor() {
    // Initialize from localStorage on service creation
    this.initFromStorage();
  }
  
  /**
   * Initialize service data from localStorage if available
   */
  private initFromStorage(): void {
    try {
      const operationData = localStorage.getItem(this.OPERATION_DATA_KEY);
      if (operationData) {
        this.operationDataSubject.next(JSON.parse(operationData));
      }
    } catch (error) {
      console.error('Error restoring data from localStorage:', error);
      // If error in parsing, clear the potentially corrupted data
      localStorage.removeItem(this.OPERATION_DATA_KEY);
    }
  }
  
  /**
   * Sets operation data in memory and localStorage
   * @param data The operation data to store
   */
  setOperationData(data: any): void {
    if (data) {
      // Store in BehaviorSubject for reactive programming
      this.operationDataSubject.next(data);
      
      // Also persist to localStorage to survive page refreshes
      localStorage.setItem(this.OPERATION_DATA_KEY, JSON.stringify(data));
      
      // Store tag number separately for easier access
      if (data.tagNumber) {
        localStorage.setItem(this.TAG_NUMBER_KEY, data.tagNumber);
      }
    }
  }
  
  /**
   * Gets the current operation data as an Observable
   * @returns An Observable of the operation data
   */
  getOperationData(): Observable<any> {
    return this.operationDataSubject.asObservable();
  }
  
  /**
   * Gets the current value of operation data synchronously 
   * @returns The current value of operation data
   */
  getOperationDataSnapshot(): any {
    // First try from memory
    let data = this.operationDataSubject.getValue();
    
    // If not available in memory, try from localStorage
    if (!data) {
      try {
        const storedData = localStorage.getItem(this.OPERATION_DATA_KEY);
        if (storedData) {
          data = JSON.parse(storedData);
          // Also update the subject
          this.operationDataSubject.next(data);
        }
      } catch (error) {
        console.error('Error reading operation data from localStorage:', error);
      }
    }
    
    return data;
  }
  
  /**
   * Gets the last used tag number
   * @returns The last used tag number or empty string
   */
  getLastTagNumber(): string {
    return localStorage.getItem(this.TAG_NUMBER_KEY) || '';
  }
  
  /**
   * Clears the operation data from memory and storage
   */
  clearOperationData(): void {
    this.operationDataSubject.next(null);
    localStorage.removeItem(this.OPERATION_DATA_KEY);
  }
  
  /**
   * Clears all stored data in the service and localStorage
   */
  clearAllData(): void {
    this.clearOperationData();
    localStorage.removeItem(this.TAG_NUMBER_KEY);
    // Clear other data subjects as they are added
  }
}